import * as Immutable from 'immutable';
import { createSelector } from 'reselect';

import { checkIfValueExists } from '../../shared/helpers';
import { StateType } from '../index';
import { SIMULATE_TUTORIAL } from '../../shared/constants';

export const activeTabIndexSelector = (state: StateType) => state.getIn(['editor', 'activeTab']) as number;
export const getTabsSelector = (state: StateType) => state.getIn(['editor', 'tabs']);
export const editorOptionsSelector = (state: StateType) => state.getIn(['editor', 'options']);
export const editorLoadedOptionsSelector = (state: StateType) => state.getIn(['editor', 'loadedOptions']);
export const editorFetchingSettingsSelector = (state: StateType) => state.getIn(['editor', 'fetchingSettings']);
export const codeEditorWidthSelector = (state: StateType) => state.getIn(['editor', 'codeEditorWidth']);
export const maxTruncationSelector = (state: StateType) => state.getIn(['editor', 'options', 'truncation', 'maxValue']);
export const minTruncationSelector = (state: StateType) => state.getIn(['editor', 'options', 'truncation', 'minValue']);
export const minDecaySelector = (state: StateType) => state.getIn(['editor', 'options', 'decay', 'minValue']);
export const getInvalidIdsSelector = (state: StateType) => state.getIn(['editor', 'invalidIds']);
export const getExampleArrayIdsSelector = (state: StateType) => state.getIn(['editor', 'exampleArrayIds']);
export const getIsMonacoInitializedSelector = (state: StateType) => state.getIn(['editor', 'isMonacoInitialized']);
export const getIsChangingTabPositionSelector = (state: StateType) => state.getIn(['editor', 'isChangingTabPosition']) as boolean;
export const editorOptionsStateSelector = createSelector(
  editorFetchingSettingsSelector,
  editorLoadedOptionsSelector,
  editorOptionsSelector,
  (isFetching, isLoaded, data) => ({
    isLoading: !isLoaded && isFetching,
    data: (data as any).toJS(),
  })
);

export const getAllExamples = (state: StateType) => state.getIn(['editor', 'examples']);

export const activeTabSelector = createSelector(
  activeTabIndexSelector,
  getTabsSelector,
  // @ts-ignore
  (index, tabs) => tabs && tabs.get(index)
);

export const getLastIndexFromTabsSelector = createSelector(
  getTabsSelector,
  // @ts-ignore
  tabs => tabs.size - 1
);

export const getCurrentLanguage = createSelector(activeTabSelector, activeTab => {
  if (activeTab) {
    return (activeTab.getIn(['settings', 'language', 'value']) && activeTab.getIn(['settings', 'language', 'value']).toLowerCase()) || '';
  } else {
    return '';
  }
});

export const isSuperALphaSelector = createSelector(activeTabSelector, activeTab => activeTab && activeTab.get('superAlpha'));

export const childTabsSelector = createSelector(activeTabSelector, tab => tab && tab.get('childTabs'));

export const selectionCodeSelector = createSelector(activeTabSelector, tab => tab && tab.get('selection'));

export const activeChildTabSelector = createSelector(activeTabSelector, activeTab => activeTab && activeTab.get('activeChildTab'));

export const currentMultiTabs = createSelector(
  activeTabSelector,
  childTabsSelector,
  (activeTab, childTabs) => (activeTab && childTabs) || Immutable.List()
);

export const currentChildActiveTabSelector = createSelector(
  activeTabSelector,
  activeChildTabSelector,
  (activeTab, activeChildTab) => (activeTab && activeChildTab) || 0
);

export const currentChildActiveTabLessOne = createSelector(activeTabSelector, activeChildTabSelector, (activeTab, activeChildTab) => {
  if (checkIfValueExists(activeTab)) {
    return activeChildTab;
  }

  return -1;
});

export const currentVisualization = createSelector(activeTabSelector, activeTab => activeTab && activeTab.get('visualization'));

export const activeTabCodeType = (state: StateType, codeType: string) => {
  const activeTab = activeTabIndexSelector(state);
  const tabs = getTabsSelector(state);

  // @ts-ignore
  return tabs && tabs.getIn([activeTab, codeType]);
};

export const activeMultiTabsCodeType = (state: StateType, codeType: string) => {
  const currentChildActive = currentChildActiveTabSelector(state);
  const childTabs = childTabsSelector(state);

  return childTabs && childTabs.getIn([currentChildActive, codeType]);
};

export const getCurrentCode = createSelector(
  activeTabIndexSelector,
  childTabsSelector,
  activeTabCodeType,
  activeMultiTabsCodeType,
  // @ts-ignore
  (index, childTabs, tabCodeType, multiTabsCodeType) => (index > -1 && childTabs === undefined ? tabCodeType : multiTabsCodeType)
);

export const settingsSelector = createSelector(activeTabSelector, tab => tab && tab.get('settings'));

export const childSettingsSelector = createSelector(childTabsSelector, currentChildActiveTabLessOne, (childTabs, indexChild) => {
  if (childTabs && indexChild >= 0) {
    return childTabs.getIn([indexChild, 'settings']);
  } else {
    return null;
  }
});

export const regionSelector = createSelector(settingsSelector, settings => settings && settings.getIn(['region', 'value']));

export const currentLanguageSelector = createSelector(settingsSelector, settings => settings && settings.getIn(['language', 'value']));

export const delaySelector = createSelector(settingsSelector, settings => settings && settings.getIn(['delay', 'value']));

export const selectionHandlingSelector = createSelector(settingsSelector, settings => {
  if (settings) {
    const instrumentType = settings.getIn(['selectionHandling', 'value']);

    return instrumentType ? instrumentType : 'POSITIVE';
  } else {
    return 'POSITIVE';
  }
});

export const instrumentTypeSelector = createSelector(settingsSelector, settings => {
  if (settings) {
    const instrumentType = settings.getIn(['instrumentType', 'value']);

    return instrumentType ? instrumentType : 'EQUITY';
  } else {
    return 'EQUITY';
  }
});

export const selectionLimitSelector = createSelector(settingsSelector, settings => settings && settings.getIn(['selectionLimit']));

export const universeSelector = createSelector(
  settingsSelector,
  childSettingsSelector,
  currentChildActiveTabLessOne,
  (settings, childSettings, currentChildActive) => {
    if (childSettings && currentChildActive >= 0) {
      return childSettings.getIn(['universe', 'value']);
    } else {
      return settings && settings.getIn(['universe', 'value']);
    }
  }
);

export const decaySelector = createSelector(
  settingsSelector,
  childSettingsSelector,
  currentChildActiveTabLessOne,
  (settings, childSettings, currentChildActive) => {
    if (childSettings && currentChildActive >= 0) {
      return childSettings.get('decay');
    } else {
      return settings && settings.get('decay');
    }
  }
);

export const truncationSelector = createSelector(
  settingsSelector,
  childSettingsSelector,
  currentChildActiveTabLessOne,
  (settings, childSettings, currentChildActive) => {
    if (childSettings && currentChildActive >= 0) {
      return childSettings.get('truncation');
    } else {
      return settings && settings.get('truncation');
    }
  }
);

export const neutralizationSelector = createSelector(
  settingsSelector,
  childSettingsSelector,
  currentChildActiveTabLessOne,
  (settings, childSettings, currentChildActive) => {
    if (childSettings && currentChildActive >= 0) {
      return childSettings.getIn(['neutralization', 'value']);
    } else {
      return settings && settings.getIn(['neutralization', 'value']);
    }
  }
);

export const pasteurizationSelector = createSelector(
  settingsSelector,
  childSettingsSelector,
  currentChildActiveTabLessOne,
  (settings, childSettings, currentChildActive) => {
    if (childSettings && currentChildActive >= 0) {
      return childSettings.getIn(['pasteurization', 'value']);
    } else {
      return settings && settings.getIn(['pasteurization', 'value']);
    }
  }
);

export const unitHandlingSelector = createSelector(
  settingsSelector,
  childSettingsSelector,
  currentChildActiveTabLessOne,
  (settings, childSettings, currentChildActive) => {
    if (childSettings && currentChildActive >= 0) {
      return childSettings.getIn(['unitHandling', 'value']);
    } else {
      return settings && settings.getIn(['unitHandling', 'value']);
    }
  }
);

export const nanHandlingSelector = createSelector(
  settingsSelector,
  childSettingsSelector,
  currentChildActiveTabLessOne,
  (settings, childSettings, currentChildActive) => {
    if (childSettings && currentChildActive >= 0) {
      return childSettings.getIn(['nanHandling', 'value']);
    } else {
      return settings && settings.getIn(['nanHandling', 'value']);
    }
  }
);

export const componentActivationSelector = createSelector(
  settingsSelector,
  settings => settings && settings.getIn(['componentActivation', 'value'])
);

export const testPeriodSelector = createSelector(
  settingsSelector,
  childSettingsSelector,
  currentChildActiveTabLessOne,
  (settings, childSettings, currentChildActive) => {
    if (childSettings && currentChildActive >= 0) {
      return childSettings.getIn(['testPeriod']);
    } else {
      return settings && settings.getIn(['testPeriod']);
    }
  }
);

export const maxTradeSelector = createSelector(
  settingsSelector,
  childSettingsSelector,
  currentChildActiveTabLessOne,
  (settings, childSettings, currentChildActive) => {
    if (childSettings && currentChildActive >= 0) {
      return childSettings.getIn(['maxTrade', 'value']);
    } else {
      return settings && settings.getIn(['maxTrade', 'value']);
    }
  }
);

export const maxPositionSelector = createSelector(
  settingsSelector,
  childSettingsSelector,
  currentChildActiveTabLessOne,
  (settings, childSettings, currentChildActive) => {
    if (childSettings && currentChildActive >= 0) {
      return childSettings.getIn(['maxPosition', 'value']);
    } else {
      return settings && settings.getIn(['maxPosition', 'value']);
    }
  }
);

const getAlphaDetailByPath = (state: StateType, props: { path: string[] }) => {
  return state.getIn(props.path.concat(['alphaDetail'])) as any;
};
const getTestStartDate = (state: StateType, props: { path: string[] }) => getAlphaDetailByPath(state, props)?.getIn(['test', 'startDate']);

export const isTestPeriodControlShownSelector = createSelector([getTestStartDate, getAlphaDetailByPath], (testStartDate, alphaDetail) => {
  return !!testStartDate && alphaDetail?.get('status') === 'UNSUBMITTED';
});

export const isTestPeriodShownSelector = (state: StateType, props: { path: string[] }): boolean => {
  return (state.getIn(props.path.concat(['alphaDetail', 'isTestPeriodShown'])) ??
    !(
      state.getIn(props.path.concat(['alphaDetail', 'status'])) === 'UNSUBMITTED' &&
      state.getIn(props.path.concat(['alphaDetail', 'test', 'startDate']))
    )) as any;
};

export const getDataExplorerPathnameSelector = (state: StateType) => state.getIn(['editor', 'dataExplorerPathname']);
export const getLearnPathnameSelector = (state: StateType) => state.getIn(['editor', 'learnPathname']);
export const getTutorialPathnameSelector = (state: StateType) => SIMULATE_TUTORIAL;
